import { createAsyncThunk, createSlice, current } from "@reduxjs/toolkit";
import { instance, pathname } from "./ActionCreators";
import axios from "axios";
import notifySuccess from 'utils/Notifications/Success'
import { RootState } from "../store";
import Error from "../../utils/Notifications/Error";
import { setActivePageName } from "./QuotesPageReducer";
import tablesSortWrapper from "../../utils/sortWorkspaces";
import { setGrid, setQuotes, setQuotesInfo } from './quotes';
import { deepEqual } from 'fast-equals';
import TickerParamsHandler from "utils/ticker-params-handler";
import { selectFilterParam } from './FilterSlice';

function updateForWorkspacesHasDiff(update: any[], state: any[]) {
    if (!Array.isArray(update) || !Array.isArray(state)) {
        return false;
    }
    for (let x = 0; x < update.length; x ++) {
        const observable = update[x];
        const from_state = state[x];
        const check = deepEqual(observable, from_state);
        if (!check) {
            return true;
        }
    }
    return false;
}

export const stylesObject = {
    optionStyles: {
        flexDirection: "row",
        flexWrap: "nowrap"
    },
    viewStyles: {
        tabWidth: "100%",
        otherTabWidth: "100%",
        otherTabRequired: false,
    }
}

type LayoutStyle = typeof stylesObject

export type IGrid = '1-0' | '2-0' | '1-1' | '3-0' | '1-2' | '4-0' | '2-2'

interface IFilter {
    query: string
    names?: string[] // A
    virt?: boolean, // B
    stopped?: boolean, // C
    close?: boolean, // D
    mm?: boolean, // E
    no_open?: boolean // F
}

export interface ISection {
    id: number
    filter: IFilter
    columns: string
    width: number
    height: number
}

export interface ICurrentTab {
    id: number,
    name: string,
    order: Array<number>
    grid: IGrid
    sections: ISection[]
}

type TabsState = {
    loading: boolean
    error: string | null
    tabs: Array<any> // вкладки
    workspaces: Array<any> // рабочие области
    workspaceToUpdateId: number | null // id пустой рабочей области, в которую мы добавляем тикеры в модалке
    tickers: Array<string> // тикеры, например AA~DEP~SPB
    addTickerPopup: Array<string> // тикеры без инструментов, например AA
    availableTickers: Array<string> // тикеры которые мы показываем в модалке выбора тикеров
    currentTab: ICurrentTab // текущая вкладка
    pickedTickers: Array<any> // выбранные тикеры из модалки создания рабочей области
    tabsNumber: number
    layoutStyle?: LayoutStyle
    optionId: '1-1' | '2-1' | '2-2' | '3-1' | '3-2' | '4-1' | '4-2',
    initialColumns: string[] // нефильтрованные столбцы параметров
    workspaceColumns: string[] // columns конкретной рабочей области
    initial_workspace_fetch_done?: boolean
}

const initialState: TabsState = {
    loading: false,
    error: null,
    tabs: [],
    workspaces: [],
    workspaceToUpdateId: null,
    tickers: [],
    addTickerPopup: [],
    availableTickers: [],
    currentTab: {} as ICurrentTab,
    pickedTickers: [],
    tabsNumber: 1,
    layoutStyle: {
        ...stylesObject
    },
    optionId: '1-1',
    initialColumns: [],
    workspaceColumns: []
}

export const updateAreaPickedTickers = createAsyncThunk('patch/workspace', async ({ workspace_id, area_id, picked_tickers }:
    { workspace_id: string | number, area_id: string | number, picked_tickers: string[] }, { dispatch, rejectWithValue }) => {
    let request;
    const picked_tickers_without_postfix = picked_tickers.map(ticker => ticker.split('~')[0]);

    const body = {
        area: workspace_id,
        filter: {
            names: picked_tickers_without_postfix
        }
    }
    try {
        request = await instance.patch(pathname + `api/workspace_config/${area_id}`, body);
        notifySuccess(`Список тикеров обновлен`)
    }
    catch(error: any) {
        return rejectWithValue(error.message);
    }
    return request.data;
})

export const getTabs = createAsyncThunk('get/tabs',
    async (_, {rejectWithValue}) => {
        try {
            const response = await instance.get(pathname + 'api/tab')

            return response.data
        } catch (e) {
            if (axios.isAxiosError(e)) {
                console.error(e.message);
            }
        }
    }
)

export const getTabById = createAsyncThunk('get/tabs/id',
    async (id: number, {rejectWithValue, dispatch, getState}) => {
        try {
            const state = getState();
            //@ts-ignore
            const active_page_id = state.quotes.active_page_id;
            if (active_page_id != id) {
                return 'active_page_id !== id';
            }
            //@ts-ignore
            if (!state.auth.loggedIn) {
                return;
            }
            const response = await instance.get(pathname + `api/tab/${id}`)
            // const sorted = {...response.data, sections: tablesSortWrapper(response.data.sections, response.data.order)}
            //dispatch(getAreasByTabId({id, order: response.data.order}))
            //console.log('da', response.data);
            if (typeof response.data === 'object') {
                if (typeof response.data.grid === 'string') {
                    dispatch(setGrid(response.data.grid));
                }
    
                if (Array.isArray(response.data.sections)) {
                    dispatch(setQuotesInfo({
                        tab_id: response.data.id,
                        workspaces: response.data.sections.map(s => s.id)
                    }));
                }

                if (Array.isArray(response.data.sections)) {
                    for (const workspace of response.data.sections) {
                        if (workspace && workspace.filter && typeof workspace.filter.query === 'string' && workspace.filter.query.length) {
                            const formulas = TickerParamsHandler.parseFormula(workspace.filter.query);
                            formulas.forEach(param => {
                                dispatch(selectFilterParam({
                                    id: workspace.id,
                                    param
                                }));
                            });
                        }
                    }
                }
            }
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message)
            }
        }
    }
)

export const getAreasByTabId = createAsyncThunk('get/areas',
    async ({id, order}: { id: number, order: Array<number> }, {rejectWithValue, getState, dispatch}) => {
        try {
            const response = await instance.get(pathname + `api/workspaces/${id}`);
            dispatch(setQuotes({ data: response.data, reference: id }));
            return tablesSortWrapper(response.data, order)
        } catch (e) {
            console.log('error', e)
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message)
            }
        }
    }
)

export const addNewWorkspace = createAsyncThunk('add/workspace',
    // @ts-ignore
    async (_, {rejectWithValue, getState}) => {
        const {tabs} = getState() as any
        const {currentTab, pickedTickers} = tabs
        const names = pickedTickers.map((ticker: string) => ticker.split('~')[0])
        try {
            const request = {
                area: currentTab.id,
                filter: {
                    query: 'A', // по сути когда мы создаем раб. область нам интересны только тикеры, которые мы отправляем в names
                    // но возможно сюда нужно будет вернуться
                    names // A
                },
                columns: 'string'
            }
            const response = await instance.post(pathname + 'api/workspace_config', request)
            notifySuccess('Вы создали рабочую область')
            return response.data
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message)
            }
        }
    }
)

export const updateWorkspace = createAsyncThunk(
    'update/workspace',
    async (_, {rejectWithValue, getState}) => {
        try {
            //@ts-ignore
            const page = getState().quotesPage.activePage
            //@ts-ignore
            const {workspaceToUpdateId: id, currentTab, pickedTickers} = getState().tabs
            const names = ["nothing"]
            const newNames = pickedTickers.map((ticker: string) => ticker.split('~')[0])

            const {filter} = currentTab.sections.find((section: ISection) => section.id === id)
            const body = {
                area: page.id,
                filter: {
                    ...filter,
                    query: 'A',
                    names: [...names, ...newNames]
                },
            }
            const response = await instance.patch(pathname + `api/workspace_config/${id}`, body)
            notifySuccess(`Список тикеров обновлен`)
            return response.data
        } catch (e) {
            console.log(e)
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message)
            }
        }
    }
)

export const updateWorkspaceColumns = createAsyncThunk<void, { id: number, columns: string | null }, { state: RootState }>(
    'update/workspaceColumns',
    async ({id, columns}, {rejectWithValue, dispatch, getState}) => {
        const {id: tabId, order} = getState().tabs.currentTab
        try {
            const body = {
                columns
            }
            const response = await instance.patch(pathname + `api/workspace_config/${id}`, body)
            notifySuccess('Настройки отображения успешно изменены')
            dispatch(getAreasByTabId({id: tabId, order}))
            return response.data
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message)
            }
        }

    }
)

export const addWorkspaceToCopiedTab = createAsyncThunk('add/workspace',
    // @ts-ignore
    async ({tabId, filter}: { tabId: number, filter: IFilter }, {rejectWithValue}) => {
        try {
            const body = {
                area: tabId,
                filter,
                columns: 'string'
            }
            const response = await instance.post(pathname + 'api/workspace_config', body)
            return response.data
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message)
            }
        }
    }
)

export const addTab = createAsyncThunk<void, { name: string }, { state: RootState }>('add/tab',
    async ({name}, {rejectWithValue, dispatch, getState}) => {
        try {
            const {tabs} = getState().tabs

            if (tabs.filter((it: { name: string; }) => it.name === name).length === 0) {
                const request = {
                    name,
                    order: null
                }
                const response = await instance.post(pathname + `api/tab`, request)
                dispatch(getTabs())
                notifySuccess(`Вкладка ${name} создана`);
                return response.data
            } else Error("Вкладка с таким названием уже существует")

        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message)
            }
        }
    }
)

export const removeTab = createAsyncThunk('remove/tab',
    async (id: number, {rejectWithValue, dispatch}) => {
        try {
            const response = await instance.delete(pathname + `api/tab/${id}`)
            dispatch(getTabs())
            return response.data
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message)
            }
        }
    }
)

export const renameTab = createAsyncThunk<any, { name: string, id: number }, { state: RootState }>('rename/tabs',
    async ({name, id}, {rejectWithValue, dispatch, getState}) => {
        try {
            const {tabs} = getState().tabs

            if (tabs.filter((it: { name: string; }) => it.name === name).length === 0) {
                const request = {
                    "name": name
                }
                const response = await instance.patch(pathname + `api/tab/${id}`, request)
                dispatch(setActivePageName(name as string))
                dispatch(getTabs())
                notifySuccess('Название вкладки изменено');
                return response.data
            } else {
                dispatch(getTabs())
                Error("Вкладка с таким названием уже существует")
            }

        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message)
            }
        }
    }
)

export const getTickers = createAsyncThunk('get/tickers',
    async (_, {rejectWithValue}) => {
        try {
            const body = {
                query: ''
            }
            const response = await instance.post(pathname + 'api/filter', body)
            return response.data
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message)
            }
        }
    }
)

export const filterTickers = createAsyncThunk('filter/tickers',
    async (_, {rejectWithValue, getState}) => {
        try {
            const {filter} = getState() as any
            const {query, paramsList} = filter
            let body = {
                query,
            }
            // @ts-ignore
            Object.entries(paramsList).map(([key, {value}]) => {
                body[key] = value
            })
            const response = await instance.post(pathname + 'api/filter', body)
            return response.data
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message)
            }
        }
    }
)

const tabsSlice = createSlice({
    name: 'TabsSlice',
    initialState,
    reducers: {
        _updateWorkspaces(state, action) {
            const { data, reference } = action.payload;
            if (!Array.isArray(data) || !reference) {
                return;
            }
            const observable = Array.isArray(state.workspaces) ? current(state.workspaces) : [];
            const some_sections_empty = !Array.isArray(data) || data.some(x => typeof x !== 'object');
            const new_workspaces = [];
            if (some_sections_empty) {
                console.log('some_sections_empty', some_sections_empty);
            }
            for (const w of data) {
                if (typeof w !== 'object' || (typeof w === 'object' && !Array.isArray(w.params))) {
                    continue;
                }
                new_workspaces.push(w);
            }

            if (new_workspaces.length < observable.length) {
                for (const w of observable) {
                    if (!new_workspaces.find(x => x.id === w.id)) {
                        new_workspaces.push(w);
                    }
                }
            }

            const has_diff = updateForWorkspacesHasDiff(data, observable);
            if (has_diff) {
                state.workspaces = new_workspaces;
            }
        },
        resetTabsState: () => {
            return initialState
        },
        resetError(state) {
            state.error = null
        },
        setTabs(state, action) {
            state.tabs = action.payload
        },
        setCurrentTab(state, action) {
            state.currentTab = action.payload
        },
        setCurrentTabName(state, action) {
            state.currentTab.name = action.payload
        },
        resetCurrentTab(state) {
            state.currentTab = {} as ICurrentTab
        },
        setPickedTickers(state, action) {
            if (typeof action.payload === 'string') {
                const tickerName = action.payload
                state.availableTickers = state.availableTickers.filter(ticker => ticker !== tickerName)
                state.pickedTickers.push(tickerName)
            } else {
                state.pickedTickers = action.payload
                state.availableTickers = state.availableTickers.filter(ticker => !state.pickedTickers.includes(ticker))
            }
        },
        resetPickedTickers(state) {
            state.pickedTickers = []
            state.availableTickers = state.tickers
        },
        setTabOption(state, action) {
            const {number, optionId} = action.payload
            state.tabsNumber = number
            state.optionId = optionId;
            // state.layoutStyle = OptionStyleSelect(optionId)
        },
        removePickedTicker(state, action) {
            const tickerName: string = action.payload
            state.pickedTickers = state.pickedTickers.filter(ticker => ticker !== tickerName)
            state.availableTickers.push(tickerName)
        },
        addAllTickers(state) {
            state.pickedTickers = state.availableTickers
            state.availableTickers = []
        },
        removeAllTickers(state) {
            state.availableTickers = state.tickers
            state.pickedTickers = []
        },
        addTickerToAddModal(state, action) {
            state.addTickerPopup.push(action.payload)
        },
        removeTickerFromAddModal(state, action) {
            state.addTickerPopup = state.addTickerPopup.filter(ticker => ticker !== action.payload)
        },
        setWorkspaceToUpdate(state, action) {
            state.workspaceToUpdateId = action.payload
        },
        resetWorkspaceToUpdate(state) {
            state.workspaceToUpdateId = null
        }
    },
    extraReducers: {
        [getTabs.fulfilled.type]: (state, action) => {
            // console.log(action.payload)
            state.tabs = action.payload
            state.loading = false
        },
        [getTabs.pending.type]: (state) => {
            state.loading = true
        },
        [getTabs.rejected.type]: (state, action) => {
            state.loading = false
            state.error = action.payload
        },
        [getTabById.fulfilled.type]: (state, action) => {
            state.currentTab = action.payload
            state.loading = false
        },
        [getTabById.pending.type]: (state) => {
            state.loading = true
        },
        [getTabById.rejected.type]: (state, action) => {
            state.loading = false
            state.error = action.payload
        },
        // [renameTab.fulfilled.type]: (state) => {
        //     state.error = null
        //     state.loading = false
        // },
        // [renameTab.pending.type]: (state) => {
        //     state.loading = true
        // },
        // [renameTab.rejected.type]: (state, action) => {
        //     state.loading = false
        //     state.error = action.payload
        // },

        // POINT

        [getAreasByTabId.fulfilled.type]: (state, action) => {
            //console.log('state.workspaces before', state.workspaces);

            state.initial_workspace_fetch_done = true;
        },
        [getAreasByTabId.pending.type]: (state) => {

        },
        [getAreasByTabId.rejected.type]: (state, action) => {
            state.error = action.payload
        },

        [getTickers.fulfilled.type]: (state, action) => {
            try {
                state.tickers = state.availableTickers = action.payload
                state.addTickerPopup = action.payload.map((ticker: string) => ticker.split('~')[0])
                state.loading = false
            } catch (e) {
                // console.error("cannot process tickers")
                state.loading = false
            }
        },
        [getTickers.pending.type]: (state) => {
            state.loading = true
        },
        [getTickers.rejected.type]: (state, action) => {
            state.loading = false
            state.error = action.payload
        },
        [filterTickers.pending.type]: (state) => {
            state.loading = true
        },
        [filterTickers.rejected.type]: (state, action) => {
            state.loading = false
            state.error = action.paylaod
        },
        [filterTickers.fulfilled.type]: (state, action) => {
            state.availableTickers = action.payload
            state.loading = false
            state.error = null
        },
        [addNewWorkspace.pending.type]: (state) => {
            state.loading = true
        },
        [addNewWorkspace.rejected.type]: (state, action) => {
            state.loading = false
            state.error = action.paylaod
        },
        [addNewWorkspace.fulfilled.type]: (state) => {
            state.pickedTickers = []
            state.loading = false
            state.error = null
        }
    }
});

export const {
    resetTabsState,
    resetError,
    setCurrentTabName,
    setPickedTickers,
    resetPickedTickers,
    removePickedTicker,
    addAllTickers,
    removeAllTickers,
    addTickerToAddModal,
    removeTickerFromAddModal,
    setWorkspaceToUpdate,
    resetWorkspaceToUpdate,
    _updateWorkspaces
} = tabsSlice.actions

export default tabsSlice.reducer

